import React, { createContext, useContext, useEffect, useState } from 'react'
import { type UserAccountData } from 'src/utils/helper'

type UserContextType = {
  currentUser: Partial<UserAccountData> | null
  isLoginComplete: boolean
  loginUser: (userData: any) => void
  setReferralStatistics: (stats: {
    betCount: string
    betVolume: string
    userCount: string
  }) => void
  setLoginCompleted: (completed: boolean) => void
  logoutUser: () => void
  isUserConnecting: boolean
  setUserConnecting: () => void
}

const UserContext = createContext<UserContextType>(null as any)

export const useUserContext = () => useContext(UserContext)

const getInitialState = () => {
  if (typeof window !== 'undefined') {
    const currentUser = localStorage.getItem('currentUser')
    return currentUser ? JSON.parse(currentUser) : null
  }
  return null
}

const getInitialLoginComplete = () => {
  if (typeof window !== 'undefined') {
    const isLoginComplete = localStorage.getItem('isLoginComplete')
    return isLoginComplete ? JSON.parse(isLoginComplete) : false
  }
  return null
}

export const UserProvider: React.CFC = ({ children }) => {
  const [currentUser, setCurrentUser] =
    useState<Partial<UserAccountData> | null>(getInitialState)

  const [isLoginComplete, setIsLoginCompleted] = useState<boolean>(
    getInitialLoginComplete
  )

  const [isUserConnecting, setIsUserConnecting] = useState<boolean>(false)

  useEffect(() => {
    localStorage.setItem('currentUser', JSON.stringify(currentUser))
  }, [currentUser])

  useEffect(() => {
    localStorage.setItem('isLoginComplete', JSON.stringify(isLoginComplete))
  }, [isLoginComplete])

  const setLoginCompleted = (completed: boolean) => {
    setIsUserConnecting(false)
    setIsLoginCompleted(completed)
  }

  const setUserConnecting = () => {
    setIsUserConnecting(true)
  }

  const loginUser = (userData: Partial<UserAccountData>) => {
    setCurrentUser(userData)
  }

  const setReferralStatistics = (stats: {
    betCount: string
    betVolume: string
    userCount: string
  }) => {
    // Update currentUser with referralStatistics
    setCurrentUser(prevState => ({
      ...prevState,
      referralStatistics: stats,
    }))
  }

  const logoutUser = () => {
    setCurrentUser(null)
    setLoginCompleted(false)
  }

  return (
    <UserContext.Provider
      value={{
        currentUser,
        loginUser,
        setReferralStatistics,
        logoutUser,
        isLoginComplete,
        setLoginCompleted,
        isUserConnecting,
        setUserConnecting,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
